import { useState } from 'react';
import { Button, toast } from '@kmgestao/ui';

import type { PublicProposalResponse } from './queries.js';
import { useApproveContract } from './queries.js';
import { AdjustmentRequestDialog } from './AdjustmentRequestDialog.js';

/** Seção "Seu contrato" exibida no fim de ProposalPublicView quando
 * proposta.status='accepted' e existe contrato gerado. Mostra PDF +
 * botões Aprovar/Solicitar ajuste + estado atual (pending, approved,
 * adjustment_requested). PR-M1-01c-ii.k-2b.
 *
 * Estados renderizados:
 *   pending              → PDF + 2 botões + texto "revise e decida"
 *   approved             → Banner "Aprovado em X" + texto "Assinafy vai
 *                          enviar email com link de assinatura — pode
 *                          demorar até 2 minutos; verificar spam"
 *   adjustment_requested → Banner amarelo "Seu ajuste está em análise" +
 *                          histórico das solicitações
 *
 * Email de assinatura: vem do **Assinafy** (não do KM Gestão). Caminho
 * mais curto: 1 clique → 1 email → assinatura. UI orienta o lead que o
 * remetente é o Assinafy pra ele não estranhar.
 */
export function ContractApprovalSection({
  token,
  contract,
}: {
  token: string;
  contract: NonNullable<PublicProposalResponse['contract']>;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmApprove, setConfirmApprove] = useState(false);
  const approve = useApproveContract();

  const handleApprove = async () => {
    try {
      const res = await approve.mutateAsync({ token });
      if (res.already_approved) {
        toast.info('Contrato já estava aprovado.', {
          description:
            res.next_step ??
            'O Assinafy já havia enviado o link de assinatura. Verifique sua caixa de entrada e a pasta de spam.',
        });
      } else {
        toast.success('Contrato aprovado!', {
          description:
            res.next_step ??
            'O Assinafy (plataforma de assinatura digital) está enviando um email com o link. Pode levar até 2 minutos.',
          duration: 8_000, // dá tempo do lead ler antes de fechar
        });
      }
      setConfirmApprove(false);
    } catch (err) {
      toast.error('Não foi possível aprovar', {
        description: err instanceof Error ? err.message : 'erro desconhecido',
      });
    }
  };

  return (
    <section className="w-full border-y border-border bg-muted/10 py-12">
      <div className="mx-auto flex max-w-4xl flex-col gap-6 px-6">
        <header className="space-y-1">
          <p className="text-xs font-medium uppercase tracking-wider text-accent">
            📄 Próximo passo
          </p>
          <h2 className="text-2xl font-semibold text-foreground">Seu contrato</h2>
          <p className="text-sm text-muted-foreground">
            Preparamos o instrumento particular com base no que você aceitou. Revise o documento e,
            se tudo estiver correto, aprove pra receber o link de assinatura digital.
          </p>
        </header>

        <StateBanner contract={contract} />

        {contract.has_pdf && contract.pdf_signed_url ? (
          <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-1">
              <p className="text-sm font-semibold text-foreground">Contrato em PDF</p>
              <p className="text-xs text-muted-foreground">
                Abra em uma nova aba pra ler com calma. Link válido por 1 hora.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button asChild variant="outline" size="sm">
                <a href={contract.pdf_signed_url} target="_blank" rel="noopener noreferrer">
                  Abrir PDF
                </a>
              </Button>
              <Button asChild variant="outline" size="sm">
                <a href={contract.pdf_signed_url} download>
                  Baixar
                </a>
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            O PDF do contrato está sendo preparado. Atualize a página em alguns segundos.
          </p>
        )}

        {contract.approval_status === 'pending' ? (
          <div className="flex flex-col gap-3 rounded-lg border border-accent/30 bg-accent/5 p-4">
            <p className="text-sm font-medium text-foreground">
              Tudo certo com o contrato? Aprove pra seguir pra assinatura digital.
            </p>
            {confirmApprove ? (
              <div className="space-y-3">
                <div className="rounded-md border border-accent/40 bg-background/60 p-3 text-sm text-foreground">
                  <p className="font-medium">Ao confirmar, você vai receber um email do Assinafy</p>
                  <p className="mt-1 text-muted-foreground">
                    O Assinafy é a plataforma de assinatura digital usada para validar o contrato. O
                    email com o link de assinatura chega em até 2 minutos no endereço cadastrado.
                    Caso não encontre, verifique a pasta de spam.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    onClick={handleApprove}
                    disabled={approve.isPending}
                    size="sm"
                  >
                    {approve.isPending ? 'Aprovando…' : 'Confirmar e receber link'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setConfirmApprove(false)}
                    disabled={approve.isPending}
                    size="sm"
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                <Button type="button" onClick={() => setConfirmApprove(true)} size="sm">
                  Aprovar contrato
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(true)}
                  size="sm"
                >
                  Solicitar ajuste
                </Button>
              </div>
            )}
          </div>
        ) : null}

        {contract.revisions.length > 0 ? <RevisionsHistory revisions={contract.revisions} /> : null}

        <AdjustmentRequestDialog open={dialogOpen} onOpenChange={setDialogOpen} token={token} />
      </div>
    </section>
  );
}

function StateBanner({ contract }: { contract: NonNullable<PublicProposalResponse['contract']> }) {
  if (contract.approval_status === 'approved' && contract.approved_at) {
    return (
      <div className="space-y-2 rounded-lg border border-accent/50 bg-accent/10 p-4 text-sm text-foreground">
        <p>
          ✅ <strong>Contrato aprovado</strong> em{' '}
          {new Date(contract.approved_at).toLocaleString('pt-BR', {
            dateStyle: 'short',
            timeStyle: 'short',
          })}
          .
        </p>
        <p className="text-muted-foreground">
          O Assinafy (plataforma de assinatura digital) está enviando um email com o link de
          assinatura para o endereço cadastrado. Pode levar até 2 minutos. Se não encontrar,
          verifique também a pasta de spam.
        </p>
      </div>
    );
  }
  if (contract.approval_status === 'adjustment_requested') {
    return (
      <div className="rounded-lg border border-warning/50 bg-warning/10 p-4 text-sm text-foreground">
        ⏳ <strong>Seu ajuste está em análise.</strong> A consultora recebeu sua solicitação e vai
        revisar o contrato. Você recebe por email quando a versão ajustada estiver pronta pra
        revisão.
      </div>
    );
  }
  return null;
}

function RevisionsHistory({
  revisions,
}: {
  revisions: NonNullable<PublicProposalResponse['contract']>['revisions'];
}) {
  return (
    <details className="rounded-lg border border-border bg-background p-4">
      <summary className="cursor-pointer text-sm font-medium text-foreground">
        Histórico de ajustes solicitados ({revisions.length})
      </summary>
      <ol className="mt-3 space-y-3">
        {revisions.map((r) => (
          <li key={r.id} className="rounded-md border border-border bg-muted/20 p-3 text-sm">
            <p className="text-xs text-muted-foreground">
              Enviado em{' '}
              {new Date(r.requested_at).toLocaleString('pt-BR', {
                dateStyle: 'short',
                timeStyle: 'short',
              })}
              {r.responded_at ? (
                <>
                  {' · '}
                  <span className="text-accent-foreground">
                    Respondido em{' '}
                    {new Date(r.responded_at).toLocaleString('pt-BR', {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })}
                  </span>
                </>
              ) : null}
            </p>
            <p className="mt-1 whitespace-pre-wrap text-foreground">{r.text}</p>
          </li>
        ))}
      </ol>
    </details>
  );
}
